import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import db, { collection, query, where, getDocs, getDoc, doc, updateDoc, serverTimestamp } from '../firebase/firestore';

// Resolves the player record for the currently signed-in guardian — every
// guardian page needs this lookup (there's no playerId custom claim), so it's
// centralized here rather than repeating the same query per page (see
// guardian/Profile.jsx, Documents.jsx, Travel.jsx, Calendar.jsx, which each
// do their own copy of it — this is the PLYRMATCH Portal's version of that).
// Passing a playerId loads that specific player instead — how agency staff
// open the same Profile/Stats pages to edit a player on their behalf (see
// admin/PlayerProfileEditor.jsx).
export function useMyPlayer(playerId = null) {
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    let cancelled = false;
    const load = playerId
      ? getDoc(doc(db, 'players', playerId)).then((d) => (d.exists() ? { id: d.id, ...d.data() } : null))
      : getDocs(query(collection(db, 'players'), where('guardianUserId', '==', user.uid)))
        .then((snap) => (snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() }));
    load.then((p) => {
      if (cancelled) return;
      if (p) setPlayer(p);
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, playerId]);

  const update = async (fields) => {
    if (!player) return;
    await updateDoc(doc(db, 'players', player.id), { ...fields, updatedAt: serverTimestamp() });
    setPlayer((p) => (p ? { ...p, ...fields } : p));
  };

  // For fields a child component already wrote to Firestore itself (e.g.
  // PlayerPhotoUpload/PlayerBannerUpload, which upload then updateDoc
  // directly) — merges into local state without a second, redundant write.
  const patchLocal = (fields) => setPlayer((p) => (p ? { ...p, ...fields } : p));

  return { player, loading, update, patchLocal };
}
